// src/modules/moduleDashboards/components/actions/IbctInboxWidget.jsx
//
// Cash sent TO this branch that is still in transit. Confirming receipt credits
// the branch drawer, so it is a real financial action taken from the dashboard —
// hence the confirmation step on Confirm and the required reason on Reject
// (which reverses the cash back to the sending branch).
//
// The confirm/reject endpoints re-check that the caller is the receiving branch;
// a row appearing here grants nothing on its own.
import React, { useState } from 'react';
import { Card, Button, Tag, Space, Typography, Modal, Input, Badge, Tooltip, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOutlined, CheckOutlined, CloseOutlined, InboxOutlined, WarningFilled,
} from '@ant-design/icons';
import { confirmTransfer, rejectTransfer } from '../../../treasury/api';
import { BranchPicker, NeedsBranch } from './BranchPicker';
import { useWidgetView } from './useWidgetView';

const { Text } = Typography;

const INK = { primary: 'rgba(0,0,0,0.88)', secondary: 'rgba(0,0,0,0.55)', muted: 'rgba(0,0,0,0.35)' };
const GRID = 'rgba(0,0,0,0.07)';
const TONE = { success: '#0f7b52', danger: '#c0362f', warning: '#b26a00', info: '#1f5fa8' };

const inr = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function IbctInboxWidget({ widgetId, title, route, data, onRefresh }) {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [busy, setBusy] = useState(null);         // id currently being confirmed
  const [done, setDone] = useState({});           // rows actioned this render
  const [reject, setReject] = useState({ open: false, row: null, reason: '', loading: false });

  // Incoming cash is per branch: the page filter seeds it, the in-card picker
  // overrides it, and only this card refetches on a switch.
  const { payload, branchId, setBranch, reload, loading } = useWidgetView({ widgetId, data });

  const target = route || '/treasury/transfers';
  const items = (payload?.items || []).filter((it) => !done[it.id]);
  const total = Math.max(0, (payload?.total || 0) - Object.keys(done).length);
  const refreshAll = () => { reload(); onRefresh?.(); };

  if (payload?.needsBranch) {
    return (
      <Card size="small" title={title} style={{ height: '100%', borderRadius: 12 }}>
        <NeedsBranch
          branches={payload.branches}
          value={branchId}
          onChange={setBranch}
          loading={loading}
          what="Incoming cash"
        />
      </Card>
    );
  }

  const doConfirm = (row) => {
    Modal.confirm({
      title: `Confirm receipt of ₹${inr(row.amount)}?`,
      content: `From ${row.fromBranch} (${row.reference}). This credits your branch cash drawer — only confirm once you physically hold the cash.`,
      okText: 'Confirm receipt',
      okButtonProps: { type: 'primary' },
      onOk: async () => {
        setBusy(row.id);
        try {
          await confirmTransfer(row.id);
          setDone((d) => ({ ...d, [row.id]: true }));
          messageApi.success(`${row.reference} received`);
          refreshAll();
        } catch (err) {
          messageApi.error(err?.response?.data?.message || 'Could not confirm receipt');
        } finally {
          setBusy(null);
        }
      },
    });
  };

  const submitReject = async () => {
    if (!reject.reason.trim()) { messageApi.warning('A reason is required'); return; }
    setReject((r) => ({ ...r, loading: true }));
    try {
      await rejectTransfer(reject.row.id, { reason: reject.reason });
      setDone((d) => ({ ...d, [reject.row.id]: true }));
      messageApi.success(`${reject.row.reference} rejected — cash reversed to sender`);
      setReject({ open: false, row: null, reason: '', loading: false });
      refreshAll();
    } catch (err) {
      setReject((r) => ({ ...r, loading: false }));
      messageApi.error(err?.response?.data?.message || 'Could not reject transfer');
    }
  };

  return (
    <Card
      size="small"
      style={{ height: '100%', borderRadius: 12, display: 'flex', flexDirection: 'column' }}
      styles={{ header: { minHeight: 40, borderBottom: `1px solid ${GRID}` }, body: { padding: 0, flex: 1 } }}
      title={(
        <Space size={8}>
          <span style={{ fontSize: 13, fontWeight: 600, color: INK.primary }}>{title}</span>
          {total > 0 && <Badge count={total} overflowCount={99} style={{ backgroundColor: TONE.warning }} />}
        </Space>
      )}
      extra={payload?.branches?.length
        ? <BranchPicker branches={payload.branches} value={branchId} onChange={setBranch} loading={loading} width={170} />
        : null}
    >
      {contextHolder}

      {items.length === 0 ? (
        <div style={{ padding: '28px 16px', textAlign: 'center' }}>
          <InboxOutlined style={{ fontSize: 26, color: TONE.success }} />
          <div style={{ marginTop: 8, color: INK.secondary, fontSize: 13 }}>No cash in transit to this branch</div>
        </div>
      ) : (
        items.map((it, i) => (
          <div key={it.id} style={{
            padding: '10px 12px', borderTop: i === 0 ? 'none' : `1px solid ${GRID}`,
            display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
          }}>
            <div style={{ flex: '1 1 180px', minWidth: 0 }}>
              <Space size={6} wrap>
                <Text strong style={{ fontSize: 15, color: TONE.info, fontVariantNumeric: 'tabular-nums' }}>
                  ₹{inr(it.amount)}
                </Text>
                <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>{it.reference}</Tag>
                {it.overdue && (
                  <Tooltip title="In transit more than a day">
                    <WarningFilled style={{ color: TONE.danger, fontSize: 12 }} />
                  </Tooltip>
                )}
              </Space>
              <Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }} ellipsis>
                from {it.fromBranch}{it.initiatedBy ? ` · sent by ${it.initiatedBy}` : ''}
              </Text>
              <Text style={{ fontSize: 11, color: it.overdue ? TONE.danger : INK.muted, display: 'block' }}>
                in transit {it.waiting}
              </Text>
            </div>

            <Space size={4} style={{ flexShrink: 0 }}>
              <Button
                type="primary" size="small" icon={<CheckOutlined />}
                loading={busy === it.id}
                onClick={() => doConfirm(it)}
              >
                Confirm
              </Button>
              <Button
                danger size="small" icon={<CloseOutlined />}
                onClick={() => setReject({ open: true, row: it, reason: '', loading: false })}
              >
                Reject
              </Button>
            </Space>
          </div>
        ))
      )}

      <div style={{ borderTop: `1px solid ${GRID}`, padding: 10 }}>
        <Button block type="primary" ghost icon={<ArrowRightOutlined />} onClick={() => navigate(target)}>
          {total > items.length ? `See all ${total} transfers` : 'Open Inter-Branch Transfer'}
        </Button>
      </div>

      <Modal
        open={reject.open}
        title={`Reject ${reject.row?.reference || 'transfer'}?`}
        okText="Reject & reverse"
        okButtonProps={{ danger: true }}
        confirmLoading={reject.loading}
        onOk={submitReject}
        onCancel={() => setReject({ open: false, row: null, reason: '', loading: false })}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          The cash is reversed back to {reject.row?.fromBranch || 'the sending branch'}. Say why — it is recorded against the transfer.
        </Text>
        <Input.TextArea
          rows={3} style={{ marginTop: 10 }}
          placeholder="Reason (required)"
          value={reject.reason}
          onChange={(e) => setReject((r) => ({ ...r, reason: e.target.value }))}
        />
      </Modal>
    </Card>
  );
}
